/**
 * "Minha agenda da semana" (spec 039), a seção da página inicial: os
 * compromissos ATRIBUÍDOS a mim, de segunda a domingo da semana corrente,
 * agrupados por dia, e no topo os "Atrasados" dos 30 dias anteriores que ainda
 * não concluíram. Quem decide o que é de quem, a semana e a situação é o
 * @erp/shared (`myWeekAgenda`, `commitmentSituation`) — aqui só a tela.
 *
 * Um pedido só: `types=commitments` dos 30 dias anteriores até domingo, sem
 * trazer os apontamentos. A caixa de "feito" é a mesma mutation otimista da
 * agenda; as duas telas dividem o cache.
 *
 * Adendo da 039: os meus agendamentos de tickets ocultos vêm SEMPRE, sem
 * switch — um agendamento só existe porque alguém o criou de propósito, e
 * sumir daqui seria perder o acompanhamento. Levam a marca "Oculto no ERP" e
 * não têm link para o ticket (o detalhe é 404).
 *
 * Adendo 3 da 039: o de período diz o período ("Tarde"), e não a hora da janela.
 *
 * Adendo 4 da 039: a caixa de "feito" é o mesmo "Marcar como feito" do
 * diálogo — gera o apontamento no ticket —, e avisa do mesmo jeito, pelo que a
 * API devolveu (`doneNotice`).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarCheck, CalendarDays } from 'lucide-react';
import {
  commitmentTimeLabel,
  isFeminineTerm,
  myWeekAgenda,
  myWeekAgendaRange,
  saoPauloDay,
  type Commitment,
} from '@erp/shared';
import { useAuthUser } from '@/auth/use-auth';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { useTransientToast } from '@/components/ui/transient-toast';
import { useTerms } from '@/shared/tenant-profile';
import { agendaEventsOptions, errorMessage } from './agenda-queries';
import { useToggleCommitmentDone } from './agenda-mutations';
import { doneErrorMessage, doneNotice } from './commitment-done';
import { capitalize, formatBRDate, parseDay } from './agenda-format';
import { CommitmentSituationBadge, SituationIcon, commitmentLabel, situationOf } from './commitment-situation';
import { HiddenTicketBadge } from './hidden-ticket-mark';
import { PlateCommitmentDrawer } from './plate-commitment-drawer';

function dayTitle(iso: string, todayISO: string): string {
  const label = capitalize(format(parseDay(iso), "EEEE, d 'de' MMMM", { locale: ptBR }));
  return iso === todayISO ? `Hoje · ${label}` : label;
}

function AgendaRow({
  c,
  now,
  showDate,
  onToggle,
  toggling,
}: {
  c: Commitment;
  now: Date;
  showDate: boolean;
  onToggle: (c: Commitment) => void;
  toggling: boolean;
}) {
  const situation = situationOf(c, now);
  const label = commitmentLabel(c);
  return (
    <li className="flex items-center gap-3 py-2">
      <Checkbox
        checked={c.done}
        disabled={toggling}
        onCheckedChange={() => onToggle(c)}
        aria-label={`Marcar ${label} como feito`}
      />
      <span className="w-20 shrink-0 text-xs text-muted-foreground tabular-nums">
        {showDate ? `${formatBRDate(c.startDate).slice(0, 5)} ` : ''}
        {commitmentTimeLabel(c)}
      </span>
      <SituationIcon situation={situation} className="size-4" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm">
          <span className="font-medium">{label}</span>{' '}
          {c.ticketHidden ? (
            <span className="text-muted-foreground">#{c.ticketNumber}</span>
          ) : (
            <Link
              to="/tickets/$ticketNumber"
              params={{ ticketNumber: String(c.ticketNumber) }}
              className="text-primary hover:text-primary-pressed hover:underline"
            >
              #{c.ticketNumber}
            </Link>
          )}
        </div>
        {c.ticketStageName ? (
          <div className="truncate text-xs text-muted-foreground">{c.ticketStageName}</div>
        ) : null}
        {c.ticketHidden ? (
          <div className="mt-0.5">
            <HiddenTicketBadge />
          </div>
        ) : null}
      </div>
      <span className="hidden sm:inline-flex">
        <CommitmentSituationBadge situation={situation} />
      </span>
    </li>
  );
}

export function MyWeekAgendaSection() {
  const user = useAuthUser();
  const terms = useTerms();
  const [scheduling, setScheduling] = useState(false);
  const toggle = useToggleCommitmentDone();
  const { toast, showToast } = useTransientToast('success', 6000);

  const now = new Date();
  const todayISO = saoPauloDay(now);
  const range = myWeekAgendaRange(now);
  // no SSO o usuário nasce com id 0 até o /me responder: `user_ids=0` viraria
  // "sem filtro" na API e traria a agenda de todo mundo
  const myId = user && user.id > 0 ? user.id : null;
  const eventsQ = useQuery({
    ...agendaEventsOptions({ ...range, userIds: myId ? [myId] : [], types: ['commitments'], includeHidden: true }),
    enabled: myId !== null,
  });
  const agenda = eventsQ.data && myId ? myWeekAgenda(eventsQ.data.commitments, myId, now) : null;
  const empty = agenda !== null && agenda.overdue.length === 0 && agenda.days.length === 0;
  const feminine = isFeminineTerm(terms.resourceId);
  const nothingThisWeek = `${feminine ? 'Nenhuma' : 'Nenhum'} ${terms.resourceId} ${feminine ? 'agendada' : 'agendado'} nesta semana.`;
  const onToggle = (c: Commitment) =>
    toggle.mutate(
      { id: c.id, done: !c.done },
      { onSuccess: (saved) => saved && showToast(doneNotice(c, saved)) }
    );

  return (
    <section className="rounded-lg border bg-card p-4 shadow-xs">
      <h2 className="mb-3 text-base font-semibold">Minha agenda da semana</h2>

      {eventsQ.isError ? (
        <EmptyState className="h-20" message={`Erro ao carregar a agenda: ${errorMessage(eventsQ.error)}`} />
      ) : agenda === null ? (
        <div className="space-y-2 py-1" aria-busy>
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : empty ? (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
          <span>{nothingThisWeek}</span>
          <Button size="sm" onClick={() => setScheduling(true)}>
            <CalendarCheck className="size-4" />
            Agendar {terms.resourceId}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {agenda.overdue.length > 0 ? (
            <div>
              <h3 className="text-sm font-semibold text-destructive">Atrasados</h3>
              <ul className="divide-y divide-divider">
                {agenda.overdue.map((c) => (
                  <AgendaRow key={c.id} c={c} now={now} showDate onToggle={onToggle} toggling={toggle.isPending} />
                ))}
              </ul>
            </div>
          ) : null}
          {agenda.days.length === 0 ? (
            <p className="text-sm text-muted-foreground">{nothingThisWeek}</p>
          ) : (
            agenda.days.map((day) => (
              <div key={day.date}>
                <h3 className="text-sm font-semibold">{dayTitle(day.date, todayISO)}</h3>
                <ul className="divide-y divide-divider">
                  {day.items.map((c) => (
                    <AgendaRow
                      key={c.id}
                      c={c}
                      now={now}
                      showDate={false}
                      onToggle={onToggle}
                      toggling={toggle.isPending}
                    />
                  ))}
                </ul>
              </div>
            ))
          )}
          {toggle.error ? (
            <p className="text-sm text-destructive">
              {doneErrorMessage(toggle.error) ?? errorMessage(toggle.error)}
            </p>
          ) : null}
        </div>
      )}

      {agenda !== null && !empty ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-divider pt-3">
          <Button variant="outline" size="sm" asChild>
            <Link to="/agenda" search={{ aba: 'recurso', view: 'week' }}>
              <CalendarDays className="size-4" />
              Ver na agenda
            </Link>
          </Button>
          <Button size="sm" onClick={() => setScheduling(true)}>
            <CalendarCheck className="size-4" />
            Agendar {terms.resourceId}
          </Button>
        </div>
      ) : null}

      {scheduling ? <PlateCommitmentDrawer onClose={() => setScheduling(false)} /> : null}
      {toast}
    </section>
  );
}
